// ---------------------------------------------------------------------------
// assembler.ts — prompt assembler + system contract (Task D5)
//
// Produces two outputs consumed by the advisor runtime (loop.ts):
//
//   assembleSystem(kb)        → SystemBlock[] (the CACHED stable prefix)
//   viewContextMessage(view, kb) → string     (per-turn VOLATILE baseline)
//
// Caching contract (spec §8.3 + global-constraints.md):
//   - The stable prefix is the same for every turn of every user session
//     because it contains NO volatile data (no view state, no history).
//   - cache_control: { type:'ephemeral' } is set on the LAST block ONLY so
//     the Anthropic relay can activate prompt caching at that breakpoint.
//   - Per-turn volatile content (viewContextMessage output) goes AFTER the
//     cache breakpoint — either as the first user message or prepended to
//     the human turn, never inside assembleSystem.
// ---------------------------------------------------------------------------

import type { KnowledgeProvider } from './types'
import type { ViewSnapshot } from './currentView'
import type { SystemBlock } from '../runtime/loop'
import { STATIC_PREFIX, SEARCH_KNOWLEDGE_HINT } from './systemText'
import type { DatasetOverview } from './datasetOverview'
import { renderOverviewBlock } from './datasetOverview'

/** Overview handed to assembleSystem: built | failed-loudly | absent (v1 tests). */
export type OverviewState = DatasetOverview | { unavailable: string } | null

// ─── assembleSystem ──────────────────────────────────────────────────────────

/**
 * Build the CACHED system prompt prefix.
 *
 * Order (per spec):
 *  1. Static role + anti-hallucination contract + tool guidance + conventions
 *  2. Routing unit body (full routing heuristics + disambiguation rules)
 *  3. Page dictionary (all 6 page titles + summaries, compact)
 *  4. search_knowledge usage hint
 *  4.5. Dataset Overview (session-stable terrain map, spec §3.4) — optional
 *  5. Knowledge version marker  <!-- knowledge <hash> -->
 *
 * Returned as a single SystemBlock with cache_control on the last (and only)
 * block. If the text is later split into multiple blocks, only the last must
 * carry cache_control (invariant enforced by the single-block design).
 */
export function assembleSystem(
  kb: KnowledgeProvider,
  overview: OverviewState = null,
): SystemBlock[] {
  const parts: string[] = []

  // 1. Static content (role + contract + tools + conventions)
  parts.push(STATIC_PREFIX)

  // 2. Routing unit body (routing heuristics + disambiguation)
  const routingUnits = kb.byType('routing')
  for (const unit of routingUnits) {
    parts.push(unit.body)
  }

  // 3. Page dictionary — compact (title + summary only; full body via search_knowledge)
  const pageUnits = kb.byType('page')
  if (pageUnits.length > 0) {
    const pageDictLines: string[] = ['## Page Dictionary\n']
    for (const unit of pageUnits) {
      pageDictLines.push(`### ${unit.title}\n${unit.summary}`)
    }
    parts.push(pageDictLines.join('\n\n'))
  }

  // 4. search_knowledge usage hint (anchored after page dictionary)
  parts.push(SEARCH_KNOWLEDGE_HINT)

  // 4.5 Dataset Overview — session-stable terrain map (spec §3.4). Rides the
  // CACHED block: it does not change within a session, so prompt caching still
  // hits every turn after the first. On build failure the model is told loudly
  // that the map is missing (it falls back to describe_page); when null
  // (feature absent / v1 tests) nothing is emitted.
  if (overview !== null) {
    if ('unavailable' in overview) {
      parts.push(
        `Dataset overview unavailable: ${overview.unavailable} — you have no pre-warmed inventory this session; use describe_page to discover pages' KPIs and dims before querying.`,
      )
    } else {
      parts.push(renderOverviewBlock(overview))
    }
  }

  // 5. Knowledge version marker (so CI / the model can detect stale caches)
  parts.push(`<!-- knowledge ${kb.version()} -->`)

  const text = parts.join('\n\n')

  // Single block — cache_control on the last (and only) block.
  return [
    {
      type: 'text',
      text,
      cache_control: { type: 'ephemeral' },
    },
  ]
}

// ─── viewContextMessage ───────────────────────────────────────────────────────

/**
 * Build the per-turn VOLATILE baseline string.
 *
 * This is NOT part of assembleSystem — it must go AFTER the cache breakpoint
 * (as a user-turn context message, not as a system block) so the stable
 * prefix remains cacheable across turns.
 *
 * Content:
 *  1. JSON.stringify(view) — the full view snapshot
 *  2. Body of the current page's knowledge unit (page:<pageId>), if present
 *  3. Body of the root KPI's knowledge unit (kpi:<root>), if present
 *
 * Units are fetched via kb.get(), which silently skips missing ids (by design).
 * The resulting string is compact: a change in any field invalidates only
 * this turn's context, NOT the cached prefix.
 */
export function viewContextMessage(view: ViewSnapshot, kb: KnowledgeProvider): string {
  const parts: string[] = []

  // 1. Serialized view snapshot
  parts.push(`## Current View\n${JSON.stringify(view)}`)

  // 2. Deterministic unit ids to fetch
  const unitIds: string[] = []
  if (view.pageId !== null && view.pageId !== undefined) {
    unitIds.push(`page:${view.pageId}`)
  }
  if (view.kpi) {
    const root = view.kpi.split('__')[0]
    if (root) {
      unitIds.push(`kpi:${root}`)
    }
  }

  // 3. Append each found unit's body with a section header
  if (unitIds.length > 0) {
    const units = kb.get(unitIds)
    for (const unit of units) {
      parts.push(`## ${unit.title} (${unit.id})\n${unit.body}`)
    }
  }

  return parts.join('\n\n')
}
